import React, { useEffect, useMemo, useState } from 'react'
import { Box, Pagination } from '@mui/material'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import CopyButton from './components/CopyButton'
import { intelColors, formatNumber } from './components/intelTheme'
import { intelApi } from './api'

const inr = (v) => '₹' + formatNumber(Math.round(v || 0))
const STAGE_BAR = { ALLOCATION: '#f57c00', COURIER: '#c62828', CONFIG: '#7b1fa2', FINANCE: '#2e7d32', DATA: '#607d8b', OTHER: '#90a4ae' }
const bar = (s) => STAGE_BAR[s] || '#90a4ae'
const PAGE_SIZE = 50
const unwrap = (res) => res?.data?.responseObj || res?.data?.data || res?.data

// Embedded view (rendered inside the Stuck Orders page via a chip — no own IntelLayout).
export default function ResolutionPlan({ merchantName = {} }) {
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [data, setData] = useState(null)
  const [stage, setStage] = useState(null) // null = all stages
  const [page, setPage] = useState(1)
  const [altsByPin, setAltsByPin] = useState({}) // pincode -> { loading } | { list } (lazy, on expand)
  const [expanded, setExpanded] = useState({})

  // Server-side paginated: only the current page (optionally one stage) is fetched.
  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .resolutionPlan({ stage: stage || undefined, page, size: PAGE_SIZE })
      .then((res) => { if (!cancel) setData(unwrap(res)) })
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => { cancel = true }
  }, [stage, page])

  const batches = useMemo(() => data?.batches || [], [data])
  const rows = useMemo(() => data?.rows || [], [data])
  const total = data?.totalRows || 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const mName = (id) => merchantName[id] || id || '—'

  // Lazy-load L1 alternatives for a drop pincode the first time a row using it is expanded.
  const loadAlts = (pincode) => {
    if (!pincode || altsByPin[pincode] !== undefined) return
    setAltsByPin((m) => ({ ...m, [pincode]: { loading: true } }))
    intelApi
      .resolutionAlternatives({ pincode })
      .then((res) => {
        const list = unwrap(res) || []
        setAltsByPin((m) => ({ ...m, [pincode]: { list: Array.isArray(list) ? list : [] } }))
      })
      .catch(() => setAltsByPin((m) => ({ ...m, [pincode]: { list: [] } })))
  }
  const toggle = (k, pincode) => {
    const willOpen = !expanded[k]
    setExpanded((e) => ({ ...e, [k]: !e[k] }))
    if (willOpen) loadAlts(pincode)
  }
  const selectStage = (st) => { setStage(st); setPage(1); setExpanded({}) }

  const cols = [
    { key: 'x', header: '' },
    { key: 'stage', header: 'Stage' },
    { key: 'ref', header: 'Order ID' },
    { key: 'merchant', header: 'Merchant' },
    { key: 'gmv', header: 'GMV' },
    { key: 'zone', header: 'Zone' },
    { key: 'mop', header: 'MOP' },
    { key: 'pin', header: 'Dest PIN' },
    { key: 'detail', header: 'Failure Detail' },
  ]

  const renderRow = (r, idx) => {
    const key = r.reference || r.orderId || `${r.stage}-${idx}`
    const open = !!expanded[key]
    const altState = altsByPin[r.pincode]
    const alts = altState?.list || []
    const altsLoading = open && r.pincode && (!altState || altState.loading)
    return (
      <React.Fragment key={key}>
        <tr onClick={() => toggle(key, r.pincode)} style={{ cursor: 'pointer' }}>
          <td style={{ width: 22 }}><span style={{ color: intelColors.muted, fontSize: 10 }}>{open ? '▾' : '▶'}</span></td>
          <td>{r.stage ? <Badge color='grey'>{r.stage}</Badge> : '—'}</td>
          <td><code style={{ fontSize: 11 }}>{r.reference || r.orderId || '—'}</code></td>
          <td>{mName(r.merchant)}</td>
          <td>{inr(r.gmv)}</td>
          <td>{r.zone ? <Badge color='grey'>{r.zone}</Badge> : '—'}</td>
          <td><Badge color={r.paymentMode === 'COD' ? 'orange' : 'green'}>{r.paymentMode || '—'}</Badge></td>
          <td><strong>{r.pincode || '—'}</strong></td>
          <td style={{ fontSize: 11, color: '#b71c1c' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', maxWidth: 300 }}>
              <Box title={r.remark || ''} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: '1 1 auto' }}>{r.remark || '—'}</Box>
              {r.remark && <CopyButton text={r.remark} title='Copy full failure detail' />}
            </Box>
          </td>
        </tr>
        {open && (
          <tr style={{ background: '#fafafa' }}>
            <td colSpan={cols.length} style={{ padding: '10px 16px' }}>
              {altsLoading ? (
                <Box sx={{ fontSize: 11.5, color: intelColors.muted }}>Loading alternatives…</Box>
              ) : !r.pincode ? (
                <Box sx={{ fontSize: 11.5, color: intelColors.muted }}>No drop pincode on this order.</Box>
              ) : alts.length === 0 ? (
                <Box sx={{ fontSize: 11.5, color: intelColors.muted }}>
                  No L1 courier serves drop PIN {r.pincode} — add coverage / upload serviceability, or route manually.
                </Box>
              ) : (
                <>
                  <Box sx={{ fontSize: 11.5, fontWeight: 700, color: intelColors.text, mb: 0.5 }}>Alternative couriers for {r.pincode}:</Box>
                  <Box component='table' sx={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse', '& th': { textAlign: 'left', padding: '4px 8px', color: '#666', fontWeight: 700 }, '& td': { padding: '4px 8px', borderTop: '1px solid #eee' } }}>
                    <thead><tr><th>Courier Code</th><th>Parent</th><th>Service Type</th><th>COD</th></tr></thead>
                    <tbody>
                      {alts.map((a, i) => (
                        <tr key={i}>
                          <td><code>{a.code}</code></td>
                          <td>{a.parent || '—'}</td>
                          <td>{a.serviceType ? <Badge color='blue'>{a.serviceType}</Badge> : '—'}</td>
                          <td>{a.cod ? <Badge color='green'>Yes</Badge> : <Badge color='grey'>No</Badge>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                  <Box sx={{ mt: 1, fontSize: 11, color: intelColors.muted }}>Assign / retry handled in the order-ops module (write action).</Box>
                </>
              )}
            </td>
          </tr>
        )}
      </React.Fragment>
    )
  }

  const card = (active, accent) => ({
    background: '#fff',
    border: `1px solid ${active ? accent : intelColors.border}`,
    borderLeft: `4px solid ${accent}`,
    borderRadius: '8px',
    p: '12px 14px',
    cursor: 'pointer',
    boxShadow: active ? `0 0 0 2px ${accent}22` : 'none',
  })

  return (
    <Box>
      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>{err}</Box>
      )}

      <Box sx={{ fontSize: 12, fontWeight: 700, color: '#37474f', textTransform: 'uppercase', letterSpacing: '.5px', mb: 1 }}>📌 Batch Resolution Actions <Box component='span' sx={{ fontWeight: 400, textTransform: 'none', color: intelColors.muted }}>— click a stage to filter the list</Box></Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', mb: 2.5 }}>
        <Box onClick={() => selectStage(null)} sx={card(stage === null, '#1976d2')}>
          <Box sx={{ fontSize: 12.5, fontWeight: 700, color: '#1976d2', mb: 0.5 }}>All Stages</Box>
          <Box sx={{ fontSize: 11.5, color: intelColors.muted }}>Every stuck order · highest-GMV first</Box>
        </Box>
        {batches.map((b) => (
          <Box key={b.stage} onClick={() => selectStage(b.stage)} sx={card(stage === b.stage, bar(b.stage))}>
            <Box sx={{ fontSize: 12.5, fontWeight: 700, color: bar(b.stage), mb: 0.5 }}>{b.label} — {formatNumber(b.count)} orders</Box>
            <Box sx={{ fontSize: 11.5, color: intelColors.muted, mb: 0.5 }}>{inr(b.gmv)} at risk · {(b.categories || []).join(', ')}</Box>
            <Box sx={{ fontSize: 11.5, color: '#555' }}>{b.actionHint}</Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ fontSize: 12, fontWeight: 700, color: '#37474f', textTransform: 'uppercase', letterSpacing: '.5px', mb: 0.5 }}>📦 Shipment-by-Shipment Resolution</Box>
      <Box sx={{ fontSize: 11, color: intelColors.muted, mb: 1.5 }}>
        {stage ? `Stage: ${stage} — ` : 'All stages — '}{formatNumber(total)} stuck orders today, highest-GMV first (server-paginated). Click a row to load L1 courier alternatives for its drop pincode.
      </Box>

      <IntelTable columns={cols} rows={rows} renderRow={renderRow} loading={loading} emptyMsg='No stuck orders to resolve.' />

      {total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ fontSize: 11.5, color: intelColors.muted }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {formatNumber(total)}
          </Box>
          <Pagination count={pageCount} page={page} onChange={(_, v) => setPage(v)} size='small' color='primary' />
        </Box>
      )}
    </Box>
  )
}
